import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

import {
  initializeMujoco,
  swapMujocoModel,
  showMujocoCanvas,
  hideMujocoCanvas,
  getMujocoApp,
} from '@/mujoco/mujocoInitializer';
import type { MujocoApp } from '@/mujoco/MujocoApp';
import type { CameraMode, Scene } from '@/types/scene.types';
import { fetchSceneActions } from '@/api/Scene';

type TarModelSource = {
  type: 'tar';
  tarPath: string;
};

type ModelSource = TarModelSource;

interface UseMujocoSimulationResult {
  app: MujocoApp | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * React hook to manage the MuJoCo simulation lifecycle.
 * @param containerElementId - The ID of the container element where the simulation will be rendered
 * @param enabled - Whether to initialize the simulation (default: true). Set to false to delay initialization.
 * @param cameraMode - The camera mode to use (default: 'top-down').
 * @param scene - Optional scene object with TAR path information
 */
export function useMujocoSimulation(
  containerElementId: string = 'mujoco-sim',
  enabled: boolean = true,
  cameraMode: CameraMode = 'top-down',
  scene?: Scene
): UseMujocoSimulationResult {
  const [app, setApp] = useState<MujocoApp | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const initializedRef = useRef(false);
  const cameraModeRef = useRef<CameraMode>(cameraMode);

  // Get URL slug to verify scene matches
  const { slug: urlSlug } = useParams<{ slug: string }>();

  // Track previous scene to detect changes
  const prevSceneRef = useRef<Scene | undefined>(scene);

  // Effect 1: Handle canvas visibility based on enabled state
  useEffect(() => {
    const existingApp = getMujocoApp();

    if (!enabled) {
      // Hide canvas when disabled
      setIsLoading(false);
      if (existingApp) {
        hideMujocoCanvas();
      }
    } else if (existingApp) {
      // Show canvas when enabled and app exists
      showMujocoCanvas();
    }
  }, [enabled]);

  // Effect 2: Handle initialization and model swapping
  useEffect(() => {
    // Don't initialize if disabled
    if (!enabled) {
      return;
    }

    setIsLoading(true);
    let mounted = true;

    // Helper to finalize initialization and update state
    const finalizeInitialization = (mujocoApp: MujocoApp, isFirstInit: boolean) => {
      if (!mounted) return;

      setApp(mujocoApp);
      setIsLoading(false);
      if (isFirstInit) {
        initializedRef.current = true;
      }
      cameraModeRef.current = cameraMode;
      prevSceneRef.current = scene;
    };

    const initOrSwap = async () => {
      // Check if the container element exists (after DOM update)
      const element = document.getElementById(containerElementId);
      if (!element) {
        setIsLoading(false);
        return;
      }

      try {
        setError(null);

        // Require a scene with tarPath
        if (!scene?.tarPath) {
          setIsLoading(false);
          return;
        }

        // **CRITICAL**: Verify scene matches URL to prevent stale scene swaps
        if (urlSlug && scene.slug !== urlSlug) {
          console.warn(
            `⚠️ Ignoring scene load: scene.slug="${scene.slug}" doesn't match URL slug="${urlSlug}"`
          );
          setIsLoading(false);
          return;
        }

        // Determine model source based on scene
        const modelSource: ModelSource = {
          type: 'tar',
          tarPath: scene.tarPath,
        };

        // Check if app already exists
        const existingApp = getMujocoApp();
        const prevScene = prevSceneRef.current;
        const sceneChanged =
          !prevScene || prevScene.id !== scene.id || prevScene.tarPath !== scene.tarPath;

        console.log('🔧 useMujocoSimulation initOrSwap:', {
          sceneId: scene.id,
          sceneName: scene.name,
          sceneSlug: scene.slug,
          tarPath: scene.tarPath,
          prevSceneId: prevScene?.id,
          prevSceneName: prevScene?.name,
          sceneChanged,
          existingApp: !!existingApp,
          initialized: initializedRef.current,
        });

        if (existingApp && initializedRef.current && existingApp.mjModel) {
          // Re-attach canvas to container if it's not already attached
          const canvas = existingApp.renderer.domElement;
          if (!element.contains(canvas)) {
            element.appendChild(canvas);
          }

          // Swap model if scene changed
          if (sceneChanged) {
            await swapMujocoModel(modelSource, cameraMode);
          } else if (cameraMode !== cameraModeRef.current) {
            // Only update camera mode if scene didn't change (swap already handles it)
            // Don't skip centering for top-down and 3rd-person modes
            // (these modes need proper positioning - top-down auto-centers, 3rd-person uses JSON config or auto-centers)
            // Only skip centering for 1st-person to preserve manual positioning
            const skipCentering = cameraMode === '1st-person';
            existingApp.setCameraMode(cameraMode, skipCentering);
          }

          // Load actions JSON if available and scene changed
          if (sceneChanged && scene.actionsJsonFile) {
            try {
              console.log(`Loading actions from ${scene.actionsJsonFile}...`);
              const actionsData = await fetchSceneActions(scene.actionsJsonFile);
              if (actionsData.actions && actionsData.actions.length > 0) {
                // Set the first action by default
                // existingApp.setMujocoActions(actionsData.actions[0]);
                existingApp.setMujocoActions(actionsData, 0);
                console.log(`Loaded ${actionsData.actions.length} actions for scene`);
              }
            } catch (err) {
              console.warn('Failed to load actions JSON:', err);
              // Don't fail the whole initialization if actions fail to load
            }
          }

          finalizeInitialization(existingApp, false);
        } else {
          // First time initialization
          const mujocoApp = await initializeMujoco(containerElementId, modelSource, cameraMode);

          // Load actions JSON if available
          if (scene.actionsJsonFile) {
            try {
              console.log(`Loading actions from ${scene.actionsJsonFile}...`);
              const actionsData = await fetchSceneActions(scene.actionsJsonFile);
              if (actionsData.actions && actionsData.actions.length > 0) {
                // Set the first action by default
                // mujocoApp.setMujocoActions(actionsData.actions[0]);
                mujocoApp.setMujocoActions(actionsData, 0);
                console.log(`Loaded ${actionsData.actions.length} actions for scene`);
              }
            } catch (err) {
              console.warn('Failed to load actions JSON:', err);
              // Don't fail the whole initialization if actions fail to load
            }
          }

          finalizeInitialization(mujocoApp, true);
        }
      } catch (err) {
        if (mounted) {
          const error = err instanceof Error ? err : new Error(String(err));
          console.error('Failed to initialize/swap MuJoCo:', error);
          setError(error);
          setIsLoading(false);
        }
      }
    };

    // Small delay to ensure DOM is fully ready
    const timeoutId = setTimeout(() => {
      initOrSwap();
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      mounted = false;
      // Don't dispose on cleanup - keep the singleton alive
    };
  }, [containerElementId, enabled, scene?.tarPath, scene?.id, cameraMode]);

  return {
    app,
    isLoading,
    error,
  };
}
